import { IsProduction } from "source/app/services/External/Environment/Environment";
import {
    GetClientDiagnosticReportText,
} from "source/app/services/ClientDiagnostics";
import { SanitizeClientDiagnosticText } from "source/app/services/ClientDiagnosticSanitization";

interface IClientErrorReport {
    clientBuild: string;
    error: string;
    errorStack: string;
    componentStack: string;
    clientLogs?: string;
    occurredAt: string;
    pageUrl: string;
    userAgent: string;
    clientRuntime: string;
    reportSource: "automatic" | "manual" | "logs-panel";
}

export async function SendClientErrorReportAsync(
    error: any,
    errorInfo: any,
    reportSource: "automatic" | "manual" = "manual"
): Promise<string> {
    return SendReportAsync(GetClientErrorReport(error, errorInfo, reportSource));
}

export async function SendClientLogsReportAsync(
    logsText: string
): Promise<string> {
    return SendReportAsync({
        ...GetClientReportContext(),
        error: "Client logs submitted from the Client logs panel",
        errorStack: "",
        componentStack: "",
        clientLogs: logsText,
        reportSource: "logs-panel",
    });
}

async function SendReportAsync(report: IClientErrorReport): Promise<string> {
    const response = await fetch(
        `${GetLogicServerBaseUrl()}client/error-report`,
        {
            method: "POST",
            mode: "cors",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(report),
        }
    );

    if (!response.ok) {
        throw new Error(`Unable to send error report (${response.status})`);
    }

    const result = await response.json();
    return result.incidentId;
}

function GetClientErrorReport(
    error: any,
    errorInfo: any,
    reportSource: "automatic" | "manual"
): IClientErrorReport {
    return {
        ...GetClientReportContext(),
        error: GetClientErrorSummary(error),
        errorStack: SanitizeClientDiagnosticText(error?.stack || "", 12000),
        componentStack: SanitizeClientDiagnosticText(
            errorInfo?.componentStack || "",
            8000
        ),
        clientLogs: GetClientDiagnosticReportText(),
        reportSource,
    };
}

function GetClientReportContext() {
    return {
        clientBuild: `${CLIENT_VERSION}+${__webpack_hash__}`,
        occurredAt: new Date().toISOString(),
        pageUrl: LimitText(`${location.origin}${location.pathname}`, 2000),
        userAgent: LimitText(navigator.userAgent, 1000),
        clientRuntime: GetClientRuntime(),
    };
}

function GetClientErrorSummary(error: any) {
    if (error?.response) {
        const status = error.response.status;
        const requestPath = GetRequestPath(error.config?.url);
        const responseData = GetResponseDataText(error.response.data);
        const requestDetails = requestPath
            ? ` ${String(error.config?.method || "GET").toUpperCase()} ${requestPath}`
            : "";

        return LimitText(
            SanitizeClientDiagnosticText(
                `HTTP ${status}${requestDetails}\n${responseData}`
            ),
            4000
        );
    }

    return LimitText(
        SanitizeClientDiagnosticText(
            error ? error.toString() : "Unknown client error"
        ),
        4000
    );
}

function GetResponseDataText(data: unknown) {
    if (typeof data === "string") {
        return data;
    }

    try {
        return JSON.stringify(data, null, 2);
    } catch (_error) {
        return String(data);
    }
}

function GetRequestPath(url: unknown) {
    if (typeof url !== "string") {
        return "";
    }

    try {
        return new URL(url, location.origin).pathname;
    } catch (_error) {
        return "";
    }
}

function GetClientRuntime() {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const connection = (navigator as any).connection;

    return LimitText(
        [
            `viewport=${window.innerWidth}x${window.innerHeight}`,
            `screen=${window.screen.width}x${window.screen.height}`,
            `dpr=${window.devicePixelRatio}`,
            `language=${navigator.language}`,
            `timezone=${timezone || "unknown"}`,
            `online=${navigator.onLine}`,
            `visibility=${document.visibilityState}`,
            `connection=${connection?.effectiveType || "unknown"}`,
        ].join("; "),
        1000
    );
}

declare const CLIENT_VERSION: string;
declare const __webpack_hash__: string;

function GetLogicServerBaseUrl() {
    return IsProduction() ? process.env.LOGIC_SERVER_URL : `${location.origin}/`;
}

function LimitText(value: string, maxLength: number) {
    return value.slice(0, maxLength);
}
